import React, { useState } from "react";

// Sean's videos — featured player loads a YouTube iframe in-app.
// `yt-nocookie.com` is used because some networks block youtube.com on classroom
// firewalls but allow the privacy-enhanced domain. If a particular video is owner-
// restricted from off-site embedding, YouTube itself renders a "Watch on YouTube"
// fallback inside the iframe — UX stays in-app.
const VIDEOS = [
  { id: "mgWiCWfvQoc", title: "Brain × Heart Loop",                              tag: "OFFICIAL CHANNEL" },
  { id: "VHiY8u3keFg", title: "Meet Sean — WGN News",                            tag: "WGN NEWS" },
  { id: "SRBaa03pQTc", title: "Exploring the Brain with Sean",                   tag: "CNN" },
  { id: "uc6JaIQlIjE", title: "Follow Your Curiosity — Re:Thinking",             tag: "TED · Adam Grant" },
  { id: "5r9xeRYKD_s", title: "Sean on FOX 10 Talks",                            tag: "FOX 10" },
  { id: "zkkyTV9tSc4", title: "Why Dance, Color & Sound Feel So Good",           tag: "Jennifer Hudson" },
];

const embedSrc = (id, autoplay = false) =>
  `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1${autoplay ? "&autoplay=1" : ""}`;

export default function SeansVideos() {
  const [active, setActive] = useState(VIDEOS[0]);
  const [playing, setPlaying] = useState(false);

  const swap = (v) => {
    setActive(v);
    setPlaying(false); // back to thumbnail so user explicitly hits play for the new clip
  };

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-12"
      id="sean-videos"
      data-testid="sean-videos"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow text-[var(--orange)]">📺 sean's channel</div>
          <h2 className="font-display text-3xl sm:text-4xl text-[var(--ink)]">Watch The Science Kid</h2>
          <p className="text-[var(--ink-soft)] mt-1 max-w-xl">
            Tap a clip below to feature it — then hit play to watch it right here.
          </p>
        </div>
        <a
          href="https://www.youtube.com/channel/UC8Gtb6QcDOTbwcsDJo1bcGg"
          target="_blank"
          rel="noreferrer noopener"
          className="btn btn-red"
          data-testid="visit-channel"
        >
          ▶ Visit Sean's Channel
        </a>
      </div>

      {/* ===== Featured player ===== */}
      <FeaturedPlayer video={active} playing={playing} onPlay={() => setPlaying(true)} />

      {/* ===== Playlist underneath ===== */}
      <div className="mt-5">
        <div className="eyebrow text-[var(--orange)] mb-2">// playlist · tap to feature</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {VIDEOS.map((v) => (
            <PlaylistItem
              key={v.id}
              video={v}
              active={v.id === active.id}
              onSelect={() => swap(v)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedPlayer({ video, playing, onPlay }) {
  const wrapperRef = React.useRef(null);
  const iframeRef  = React.useRef(null);

  // When the device rotates to landscape WHILE a video is playing, auto-fullscreen
  // the iframe so the video fills the screen (matches native YouTube behaviour).
  React.useEffect(() => {
    if (!playing) return;
    const tryFullscreen = () => {
      const el = iframeRef.current;
      if (!el) return;
      const isLandscape =
        (window.screen.orientation && window.screen.orientation.type?.startsWith("landscape")) ||
        window.innerWidth > window.innerHeight * 1.2;
      const inFs =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      if (isLandscape && !inFs) {
        const req =
          el.requestFullscreen ||
          el.webkitRequestFullscreen ||
          el.webkitEnterFullscreen || // iOS Safari
          el.msRequestFullscreen;
        try { req && req.call(el); } catch {/* user-gesture required on some browsers */}
      }
    };
    const onChange = () => tryFullscreen();
    window.addEventListener("orientationchange", onChange);
    window.addEventListener("resize", onChange);
    if (window.screen.orientation) {
      window.screen.orientation.addEventListener("change", onChange);
    }
    // Also try immediately in case the user is already in landscape when they press play
    setTimeout(tryFullscreen, 100);
    return () => {
      window.removeEventListener("orientationchange", onChange);
      window.removeEventListener("resize", onChange);
      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener("change", onChange);
      }
    };
  }, [playing]);

  return (
    <div
      ref={wrapperRef}
      className="card tint-orange"
      style={{ padding: "0.7rem", paddingBottom: "1rem" }}
      data-testid="featured-player"
    >
      <div
        className="relative w-full overflow-hidden rounded-xl"
        style={{ aspectRatio: "16 / 9", border: "2.5px solid var(--ink)" }}
      >
        {playing ? (
          <iframe
            ref={iframeRef}
            key={video.id}
            src={embedSrc(video.id, true)}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            style={{ border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid={`featured-iframe-${video.id}`}
          />
        ) : (
          <button
            type="button"
            onClick={onPlay}
            aria-label={`Play ${video.title}`}
            data-testid={`featured-play-${video.id}`}
            className="absolute inset-0 w-full h-full group p-0 m-0 cursor-pointer"
            style={{ background: "transparent", border: 0 }}
          >
            <img
              src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
              alt={video.title}
              className="w-full h-full object-cover block transition-transform duration-300 group-hover:scale-[1.03]"
              referrerPolicy="no-referrer"
            />
            <div
              aria-hidden
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55) 100%)" }}
            >
              <div
                className="flex items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
                style={{
                  width: 88, height: 88,
                  background: "var(--red)",
                  border: "3.5px solid #fff",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
                }}
              >
                <span style={{ color: "#fff", fontSize: "2rem", marginLeft: 6 }}>▶</span>
              </div>
            </div>
            <span
              className="absolute top-3 left-3 pill"
              style={{ background: "var(--ink)", color: "#fff", fontSize: "0.66rem", padding: "0.3rem 0.75rem" }}
            >
              {video.tag}
            </span>
          </button>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 px-1">
        <div className="font-display text-lg sm:text-xl text-[var(--ink)] leading-tight">
          {video.title}
        </div>
        <a
          href={`https://www.youtube.com/watch?v=${video.id}`}
          target="_blank"
          rel="noreferrer noopener"
          className="btn-ghost text-xs shrink-0"
          style={{ padding: "0.4rem 0.8rem" }}
          data-testid="featured-yt-link"
          title="Open this video on YouTube"
        >
          YouTube ↗
        </a>
      </div>
    </div>
  );
}

function PlaylistItem({ video, active, onSelect }) {
  return (
    <button
      onClick={onSelect}
      data-testid={`playlist-${video.id}`}
      aria-pressed={active}
      aria-label={`Feature ${video.title}`}
      className="text-left block card-flat"
      style={{
        padding: "0.45rem",
        background: active ? "var(--sun)" : "#fff",
        borderColor: "var(--ink)",
        boxShadow: active ? "4px 4px 0 0 var(--ink)" : "2px 2px 0 0 var(--ink)",
        transform: active ? "translate(-1px,-1px)" : "none",
        transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
        cursor: "pointer",
      }}
    >
      <div
        className="relative w-full overflow-hidden rounded-lg"
        style={{ aspectRatio: "16 / 9", border: "2px solid var(--ink)" }}
      >
        <img
          src={`https://i.ytimg.com/vi/${video.id}/mqdefault.jpg`}
          alt={video.title}
          className="w-full h-full object-cover block"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
        {active && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ background: "rgba(255,119,23,0.25)" }}
            aria-hidden
          >
            <span
              className="pill"
              style={{
                background: "var(--orange)",
                color: "#fff",
                fontSize: "0.6rem",
                padding: "0.2rem 0.55rem",
              }}
            >
              ● NOW PLAYING
            </span>
          </div>
        )}
      </div>
      <div className="mt-1.5 text-[0.75rem] font-bold text-[var(--ink)] leading-tight line-clamp-2">
        {video.title}
      </div>
    </button>
  );
}
